import React from 'react';
import { motion } from 'framer-motion';
import {
    Maximize2,
    Brain,
    Users,
    PenTool,
    Layout,
    Play,
    Sparkles,
    Hand,
    Search,
    Command
} from 'lucide-react';

export const FeaturesSection: React.FC = () => {
    const variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <section id="features" className="features-section">
            <div className="section-header">
                <span className="section-label">Features</span>
                <h2 className="section-title">Designed for Flow</h2>
                <p className="text-slate-300 max-w-2xl mx-auto text-lg">
                    A workspace that adapts to your thinking process, not the other way around.
                </p>
            </div>

            <div className="bento-grid">
                {/* Card 1: Infinite Canvas (Large) */}
                <motion.div
                    className="bento-card col-span-2 h-[400px] group overflow-hidden"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5 }}
                >
                    <div className="bento-icon relative z-20">
                        <Maximize2 className="w-6 h-6" />
                    </div>

                    {/* Mini Canvas UI - 200x Improved */}
                    <div className="bento-visual">
                        <div className="absolute inset-0 bg-[#0a0a0f]" />

                        {/* Infinite Grid with Radial Fade */}
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage: 'radial-gradient(#6366f1 1px, transparent 1px)',
                                backgroundSize: '24px 24px',
                                maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
                            }}
                        />

                        {/* Connection Line (Bezier) */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                            <path
                                d="M 180 120 Q 280 120 320 180"
                                fill="none"
                                stroke="url(#gradient-line)"
                                strokeWidth="3"
                                className="opacity-50 group-hover:opacity-100 transition-opacity duration-500"
                                strokeDasharray="6 4"
                            />
                            <defs>
                                <linearGradient id="gradient-line" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#818cf8" />
                                    <stop offset="100%" stopColor="#c084fc" />
                                </linearGradient>
                            </defs>
                        </svg>

                        {/* Note 1: Sticky Note (Left) */}
                        <div className="absolute top-16 left-20 w-48 h-32 bg-gradient-to-br from-indigo-900/80 to-slate-900/80 border border-indigo-500/30 rounded-xl p-4 shadow-2xl transform -rotate-3 group-hover:rotate-0 transition-transform duration-500 backdrop-blur-md z-10 group-hover:scale-105">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-1.5 bg-indigo-400 rounded-full" />
                            </div>
                            <div className="space-y-2">
                                <div className="h-1.5 w-full bg-white/10 rounded-full" />
                                <div className="h-1.5 w-5/6 bg-white/10 rounded-full" />
                                <div className="h-1.5 w-3/4 bg-white/10 rounded-full" />
                            </div>
                            {/* Selection Ring */}
                            <div className="absolute -inset-[2px] border-2 border-indigo-500/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>

                        {/* Note 2: Image/Media (Right) - Draggable Illusion */}
                        <div className="absolute top-32 left-80 w-52 h-40 bg-gradient-to-br from-slate-900/90 to-black/90 border border-white/10 rounded-xl p-3 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transform rotate-6 group-hover:translate-x-2 group-hover:rotate-3 transition-all duration-500 z-10">
                            <div className="w-full h-24 bg-indigo-500/10 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                                <div className="w-12 h-12 rounded-full bg-indigo-500/20 blur-xl absolute" />
                                <Play className="w-8 h-8 text-indigo-400 opacity-80" />
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                                    <span className="text-[10px] text-purple-300">J</span>
                                </div>
                                <div className="h-1.5 w-20 bg-white/10 rounded-full" />
                            </div>
                        </div>

                        {/* Cursor Hand - Grabbing the Right Note */}
                        <div className="absolute top-[160px] left-[460px] transform -rotate-12 group-hover:scale-90 group-hover:translate-x-[-5px] group-hover:translate-y-[-5px] transition-all duration-300 z-50">
                            <Hand className="w-8 h-8 text-white fill-white/20 drop-shadow-2xl filter" />
                        </div>

                        {/* Floating Toolbar - Centered & Higher to avoid text */}
                        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full shadow-2xl backdrop-blur-xl z-20">
                            {['cursor', 'pen', 'shapes', 'image'].map((i, idx) => (
                                <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-white/10 ${idx === 1 ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>
                                    {idx === 0 && <Maximize2 className="w-4 h-4" />}
                                    {idx === 1 && <PenTool className="w-4 h-4" />}
                                    {idx === 2 && <Layout className="w-4 h-4" />}
                                    {idx === 3 && <Sparkles className="w-4 h-4" />}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bento-content relative z-20">
                        <h3>Infinite Canvas</h3>
                        <p>A limitless workspace for spatial organization. Drag, drop, and connect ideas without boundaries.</p>
                    </div>
                </motion.div>

                {/* Card 2: AI (Tall) */}
                <motion.div
                    className="bento-card row-span-2 group"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <div className="bento-icon">
                        <Brain className="w-6 h-6" />
                    </div>

                    {/* Mini Smart View UI */}
                    <div className="bento-visual p-6 pt-20">
                        <div className="w-full h-full bg-[#0F0F13] border border-white/10 rounded-xl overflow-hidden flex flex-col shadow-2xl relative">
                            {/* Header */}
                            <div className="h-10 border-b border-white/5 bg-white/5 flex items-center px-3 gap-2">
                                <div className="w-2 h-2 rounded-full bg-slate-600" />
                                <div className="h-1.5 w-20 bg-white/10 rounded ml-2" />
                            </div>
                            {/* Chat Area */}
                            <div className="flex-1 p-3 space-y-3">
                                {/* User Msg */}
                                <div className="self-end bg-indigo-600/20 border border-indigo-500/20 rounded-lg p-2 ml-auto max-w-[80%]">
                                    <div className="h-1.5 w-16 bg-indigo-200/50 rounded" />
                                </div>
                                {/* AI Msg */}
                                <div className="self-start bg-white/5 border border-white/5 rounded-lg p-2 max-w-[90%]">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-3 h-3 text-indigo-400" />
                                        <div className="h-1.5 w-12 bg-indigo-400/30 rounded" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="h-1 w-full bg-slate-600 rounded" />
                                        <div className="h-1 w-full bg-slate-600 rounded" />
                                        <div className="h-1 w-2/3 bg-slate-600 rounded" />
                                    </div>
                                </div>
                            </div>
                            {/* Input Area */}
                            <div className="p-3 border-t border-white/5">
                                <div className="h-8 bg-white/5 rounded-full w-full" />
                            </div>

                            {/* Floating Flashcard Overlay */}
                            <div className="absolute bottom-20 right-4 w-32 aspect-[3/2] bg-white dark:bg-[#1e1e1e] border border-white/10 rounded-lg shadow-xl transform rotate-6 group-hover:rotate-0 transition-all duration-300">
                                <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-t-lg" />
                                <div className="p-3 flex items-center justify-center h-full">
                                    <span className="text-[10px] text-slate-400 font-mono">Flashcard Generated</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-content">
                        <h3>Smart View AI</h3>
                        <p>Instant summarization, flashcard generation, and interactive study guides from your notes.</p>
                    </div>
                </motion.div>

                {/* Card 3: Real-time Collab */}
                <motion.div
                    className="bento-card group"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="bento-icon">
                        <Users className="w-6 h-6" />
                    </div>

                    {/* Mini Cursors UI */}
                    <div className="bento-visual">
                        <div className="absolute top-1/4 left-1/4 w-4 h-4">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transform -rotate-12 drop-shadow-lg">
                                <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19177L11.7841 12.3673H5.65376Z" fill="#EC4899" stroke="white" strokeWidth="1" />
                            </svg>
                            <div className="absolute left-3 top-3 bg-pink-500 px-2 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap">Veronica</div>
                        </div>

                        <div className="absolute top-1/3 right-1/4 w-4 h-4 group-hover:translate-x-4 group-hover:-translate-y-4 transition-transform duration-700">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transform -rotate-12 drop-shadow-lg">
                                <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19177L11.7841 12.3673H5.65376Z" fill="#3B82F6" stroke="white" strokeWidth="1" />
                            </svg>
                            <div className="absolute left-3 top-3 bg-blue-500 px-2 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap">Jazz</div>
                        </div>
                    </div>

                    <div className="bento-content">
                        <h3>Real-time Sync</h3>
                        <p>Multiplayer whiteboard for team brainstorming. See cursors and edits live.</p>
                    </div>
                </motion.div>

                {/* Card 4: Drawing */}
                <motion.div
                    className="bento-card group overflow-hidden relative"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <div className="bento-icon relative z-20">
                        <PenTool className="w-6 h-6" />
                    </div>

                    <div className="bento-visual">
                        {/* Designer Dot Grid Background */}
                        <div
                            className="absolute inset-0 opacity-[0.15]"
                            style={{
                                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                            }}
                        />

                        <div className="absolute inset-0 flex items-center justify-center p-8">
                            <div className="relative w-full h-full max-w-[280px] max-h-[180px]">
                                {/* Animated SVG Drawing */}
                                <svg width="100%" height="100%" viewBox="0 0 280 180" className="drop-shadow-[0_0_15px_rgba(99,102,241,0.3)]">
                                    {/* Ghost Path (Underneath) */}
                                    <path
                                        d="M40,140 C40,140 60,40 140,90 C220,140 240,40 240,40"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="4"
                                        className="opacity-[0.05]"
                                    />
                                    {/* Self-Drawing Path */}
                                    <motion.path
                                        d="M40,140 C40,140 60,40 140,90 C220,140 240,40 240,40"
                                        fill="none"
                                        stroke="url(#draw-gradient)"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        transition={{ duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }}
                                    />
                                    <defs>
                                        <linearGradient id="draw-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#6366f1" />
                                            <stop offset="50%" stopColor="#a855f7" />
                                            <stop offset="100%" stopColor="#ec4899" />
                                        </linearGradient>
                                    </defs>

                                    {/* Following Pen Icon */}
                                    <motion.g
                                        initial={{ offsetDistance: "0%" }}
                                        animate={{ offsetDistance: "100%" }}
                                        transition={{ duration: 3, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }}
                                        style={{ offsetPath: "path('M40,140 C40,140 60,40 140,90 C220,140 240,40 240,40')", offsetRotate: "auto" }}
                                    >
                                        <PenTool className="w-5 h-5 text-white/80 -translate-x-1/2 -translate-y-full drop-shadow-xl" />
                                    </motion.g>
                                </svg>

                                {/* Floating Drawing Toolbar */}
                                <motion.div
                                    className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-3 py-1.5 flex items-center gap-2.5 shadow-2xl z-30"
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1 }}
                                >
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-indigo-500 ring-2 ring-white/20" />
                                        <div className="w-3 h-3 rounded-full bg-pink-500" />
                                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                    </div>
                                    <div className="w-px h-3 bg-white/10" />
                                    <div className="flex gap-1.5 opacity-60">
                                        <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                        <div className="w-2.5 h-2.5 bg-white rounded-full scale-110" />
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-content relative z-20">
                        <h3 className="text-xl font-bold text-white mb-2">Pro Drawing</h3>
                        <p className="text-slate-400 text-sm leading-relaxed max-w-sm">Vector-perfect tools with zero lag. Sketch, diagram, and annotate your vision.</p>
                    </div>
                </motion.div>

                {/* Card 5: Note Types (Wide) */}
                <motion.div
                    className="bento-card col-span-2 group min-h-[420px] overflow-hidden"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <div className="bento-icon relative z-30">
                        <Layout className="w-6 h-6" />
                    </div>

                    <div className="bento-visual overflow-visible">
                        {/* High-Fidelity Floating Mosaic */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="relative w-full h-full max-w-[500px] max-h-[300px]">

                                {/* 1. Code Snippet (Monokai Style) */}
                                <motion.div
                                    className="absolute top-0 right-[15%] w-64 bg-[#1a1b26]/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-4 z-20"
                                    initial={{ opacity: 0, x: 20, rotate: 2 }}
                                    whileInView={{ opacity: 1, x: 0, rotate: 2 }}
                                    whileHover={{ scale: 1.05, rotate: 0, zIndex: 50, transition: { duration: 0.2 } }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <div className="flex gap-1.5 mb-3 opacity-50">
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                                    </div>
                                    <div className="font-mono text-[10px] leading-relaxed">
                                        <p><span className="text-purple-400">const</span> <span className="text-blue-400">flow</span> = <span className="text-orange-400">async</span> () <span className="text-purple-400">=&gt;</span> {'{'}</p>
                                        <p className="pl-4 text-slate-400"><span className="text-purple-400">await</span> brainstorm();</p>
                                        <p className="pl-4 text-emerald-400"><span className="text-slate-400">return</span> <span className="text-emerald-400">"unlocked"</span>;</p>
                                        <p>{'}'};</p>
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-full h-full border border-indigo-500/20 rounded-xl -z-10" />
                                </motion.div>

                                {/* 2. Kanban Snippet */}
                                <motion.div
                                    className="absolute -bottom-4 left-[10%] w-56 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-4 z-10"
                                    initial={{ opacity: 0, x: -20, rotate: -3 }}
                                    whileInView={{ opacity: 1, x: 0, rotate: -3 }}
                                    whileHover={{ scale: 1.05, rotate: 0, zIndex: 50, transition: { duration: 0.2 } }}
                                    transition={{ delay: 0.6 }}
                                >
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="h-2 w-16 bg-white/20 rounded-full" />
                                        <div className="w-5 h-5 rounded-full bg-slate-700 border border-white/20" />
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-white/5 border border-white/5 rounded-lg p-2.5">
                                            <div className="h-1.5 w-full bg-white/10 rounded-full mb-2" />
                                            <div className="flex gap-1.5">
                                                <div className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-[8px] text-emerald-400 font-bold uppercase tracking-tighter">Done</div>
                                                <div className="w-3 h-3 rounded-full bg-indigo-500/30" />
                                            </div>
                                        </div>
                                        <div className="bg-white/10 border border-white/10 rounded-lg p-2.5 transform translate-x-2">
                                            <div className="h-1.5 w-4/5 bg-white/10 rounded-full mb-2" />
                                            <div className="px-1.5 py-0.5 rounded bg-amber-500/20 text-[8px] text-amber-400 font-bold uppercase tracking-tighter w-fit">In Review</div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* 3. Math / LaTeX Snippet */}
                                <motion.div
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 aspect-square bg-white dark:bg-black/40 backdrop-blur-3xl border border-white/20 rounded-full shadow-[0_0_50px_rgba(99,102,241,0.2)] z-30 flex items-center justify-center group/math"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    transition={{ delay: 0.7, type: "spring", stiffness: 100 }}
                                >
                                    <div className="text-center">
                                        <p className="text-2xl font-serif text-white/90 italic tracking-widest group-hover/math:text-indigo-400 transition-colors">e<sup>iπ</sup> + 1 = 0</p>
                                        <p className="text-[10px] text-slate-500 font-medium mt-1 uppercase tracking-[0.2em]">Euler's Identity</p>
                                    </div>
                                    <div className="absolute inset-0 rounded-full border border-indigo-500/30 animate-pulse" />
                                </motion.div>

                                {/* 4. Media Player Snippet */}
                                <motion.div
                                    className="absolute bottom-10 right-[5%] w-52 bg-slate-900/40 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-2xl z-20 group/media"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                                    transition={{ delay: 0.8 }}
                                >
                                    <div className="w-full h-24 bg-gradient-to-tr from-indigo-900 via-indigo-600 to-purple-800 relative flex items-center justify-center px-4">
                                        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover/media:scale-110 transition-transform">
                                            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                                        </div>
                                        <div className="absolute bottom-2 left-3 right-3 h-1 bg-white/20 rounded-full overflow-hidden">
                                            <motion.div
                                                className="h-full bg-white"
                                                initial={{ width: "0%" }}
                                                whileInView={{ width: "65%" }}
                                                transition={{ duration: 2, delay: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-2 px-3 flex justify-between items-center">
                                        <div className="h-2 w-20 bg-white/20 rounded-full" />
                                        <Sparkles className="w-3 h-3 text-indigo-400" />
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-content relative z-30">
                        <h3 className="text-xl font-bold text-white mb-2">13+ Note Types</h3>
                        <p className="text-slate-400 text-sm leading-relaxed max-w-sm">From Monokai code snippets and LaTeX formulas to Kanban boards and media players. Structure your chaos.</p>
                    </div>
                </motion.div>

                {/* Card 6: Spotlight Search (Square) */}
                <motion.div
                    className="bento-card group overflow-hidden relative"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={variants}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    <div className="bento-icon relative z-20">
                        <Search className="w-6 h-6" />
                    </div>

                    <div className="bento-visual p-6 flex items-center justify-center">
                        <div className="relative w-full max-w-[180px] space-y-3">
                            {/* Floating Command Palette Bar */}
                            <motion.div
                                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg p-2.5 flex items-center justify-between shadow-2xl relative overflow-hidden"
                                whileHover={{ scale: 1.02 }}
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse" />
                                    <span className="text-[10px] font-medium text-slate-300">Searching...</span>
                                </div>
                                <div className="flex items-center gap-1 opacity-40">
                                    <Command className="w-3 h-3 text-white" />
                                    <span className="text-[10px] font-bold text-white">K</span>
                                </div>
                                {/* Scanning Line Animation */}
                                <motion.div
                                    className="absolute inset-y-0 w-8 bg-gradient-to-r from-transparent via-indigo-500/10 to-transparent"
                                    animate={{ x: [-100, 200] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                />
                            </motion.div>

                            {/* Active Search Result */}
                            <motion.div
                                className="bg-indigo-600/10 border border-indigo-500/20 rounded-lg p-2 flex items-center gap-3"
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.8 }}
                            >
                                <div className="w-8 h-8 rounded-md bg-indigo-500/20 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-indigo-400" />
                                </div>
                                <div className="space-y-1">
                                    <div className="h-1.5 w-20 bg-indigo-300/30 rounded-full" />
                                    <div className="h-1 w-12 bg-indigo-300/10 rounded-full" />
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    <div className="bento-content relative z-20">
                        <h3 className="text-xl font-bold text-white mb-1">Spotlight Search</h3>
                        <p className="text-slate-400 text-sm">Find any note or connection instantly.</p>
                    </div>

                    {/* Background Glow */}
                    <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                </motion.div>

            </div>
        </section>
    );
};
